import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * The analytics reads behind Översikt's Besök, Varifrån besökarna kommer and Mest besökta sidor.
 *
 * Since build round 4 (2026-09-27) there is no `/analytics`: the figures are on the overview
 * (plan.md section 5.1) and they are what decision 0387 set in section 5.5, aggregate only. The column
 * lists below are the whole of what `authenticated` is granted, and the columns it is not granted are
 * the point: `last_error_at` and `zone_identifier` are not readable here, so the interface has nothing
 * to say about why a connection is failing even if it wanted to. The failure creates an internal
 * integration warning instead, and the client gets a neutral state.
 *
 * `analytics_snapshots.page_views` is not read, since 2026-09-26: `authenticated` lost the column
 * grant and nothing writes it any more, so selecting it would fail the whole read. Page views survive
 * per page, in `analytics_page_metrics`, which is where the client sees them. The four fields round 3
 * added, `visits_previous`, `mobile_share`, `speed_rating` and `sources`, are granted and read here
 * for the first time (migration 20260926100001).
 *
 * There is at most one snapshot per connection. `dispatch_replace_analytics_snapshot` deletes
 * before it inserts, because the period is a rolling thirty days that moves every day and appending
 * would leave one expired row per day for ever.
 */

export const analyticsConnectionsKey = (tenantId: string) =>
  ["portal", "analytics-connections", tenantId] as const;
export const analyticsSnapshotsKey = (tenantId: string) =>
  ["portal", "analytics-snapshots", tenantId] as const;
export const analyticsPageMetricsKey = (tenantId: string) =>
  ["portal", "analytics-page-metrics", tenantId] as const;
export const analyticsMonthlyVisitsKey = (tenantId: string) =>
  ["portal", "analytics-monthly-visits", tenantId] as const;

export interface AnalyticsConnection {
  id: string;
  service_id: string;
  provider: string;
  status: string;
  last_success_at: string | null;
}

/** One referring source as the Worker stores it: a null host is a visit with no referrer. */
export interface AnalyticsSource {
  host: string | null;
  visits: number;
}

export interface AnalyticsSnapshot {
  id: string;
  analytics_connection_id: string;
  period_start: string;
  period_end: string;
  visits: number;
  /** The 30 days before the period, from the same zone dataset. Null before round 3's first refresh. */
  visits_previous: number | null;
  /** Percent of visits on a phone, from Web Analytics. Null without a site tag. */
  mobile_share: number | null;
  /** `good`, `needs_improvement` or `poor`, from Web Analytics. Null without a site tag. */
  speed_rating: string | null;
  sources: AnalyticsSource[];
  /**
   * When the figures were fetched from the provider, not when they were rendered. A number with no
   * capture time reads as live, and this cache may be up to twenty four hours old by construction.
   */
  captured_at: string;
}

export interface AnalyticsPageMetric {
  id: string;
  analytics_snapshot_id: string;
  page_path: string;
  page_views: number;
  rank: number;
}

/** One stored visit total for a calendar month. `month` is the first day of the month. */
export interface AnalyticsMonthlyVisits {
  id: string;
  analytics_connection_id: string;
  month: string;
  visits: number;
}

export function useAnalyticsConnections(
  tenantId: string | null
): UseQueryResult<AnalyticsConnection[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, analyticsConnectionsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<AnalyticsConnection>("admin_as_client_analytics_connections", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("analytics_connections")
        .select("id, service_id, provider, status, last_success_at");

      if (error) {
        throw error;
      }
      return (data ?? []) as AnalyticsConnection[];
    }
  });
}

/**
 * `sources` is `jsonb` and arrives untyped, so it is narrowed here rather than trusted: a row that is
 * not `{host, visits}` is dropped, which the check constraint on the column makes unlikely and which
 * would otherwise be a label reading "undefined" on a client's screen.
 */
function readSources(value: unknown): AnalyticsSource[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) {
      return [];
    }
    const { host, visits } = entry as { host?: unknown; visits?: unknown };
    if ((host !== null && typeof host !== "string") || typeof visits !== "number" || visits < 0) {
      return [];
    }
    return [{ host: host as string | null, visits }];
  });
}

export function useAnalyticsSnapshots(
  tenantId: string | null
): UseQueryResult<AnalyticsSnapshot[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, analyticsSnapshotsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<Omit<AnalyticsSnapshot, "sources"> & { sources: unknown }>(
          "admin_as_client_analytics_snapshots",
          { p_tenant_id: source.tenantId }
        );
        return rows.map((row) => ({ ...row, sources: readSources(row.sources) }));
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("analytics_snapshots")
        .select(
          "id, analytics_connection_id, period_start, period_end, visits, visits_previous, mobile_share, speed_rating, sources, captured_at"
        )
        .order("period_end", { ascending: false })
        .order("captured_at", { ascending: false });

      if (error) {
        throw error;
      }
      return (data ?? []).map((row) => ({ ...row, sources: readSources(row.sources) })) as AnalyticsSnapshot[];
    }
  });
}

export function useAnalyticsPageMetrics(
  tenantId: string | null
): UseQueryResult<AnalyticsPageMetric[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, analyticsPageMetricsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        return viewRows<AnalyticsPageMetric>("admin_as_client_analytics_page_metrics", { p_tenant_id: source.tenantId });
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("analytics_page_metrics")
        .select("id, analytics_snapshot_id, page_path, page_views, rank")
        .order("rank", { ascending: true });

      if (error) {
        throw error;
      }
      return (data ?? []) as AnalyticsPageMetric[];
    }
  });
}

/**
 * The monthly totals, the one analytics table that is not a 24-hour cache (plan.md section 5.5, the
 * exception added 2026-09-26). Thirteen months per connection at most are needed for twelve bars, and
 * a connection older than that has more rows, so the newest are taken.
 */
export function useAnalyticsMonthlyVisits(
  tenantId: string | null
): UseQueryResult<AnalyticsMonthlyVisits[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, analyticsMonthlyVisitsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<AnalyticsMonthlyVisits>("admin_as_client_analytics_monthly_visits", {
          p_tenant_id: source.tenantId
        });
        // The gateway answers every month newest first; the client's limit of 120 is applied here.
        return rows
          .slice(0, 120)
          .map(({ id, analytics_connection_id, month, visits }) => ({ id, analytics_connection_id, month, visits }));
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("analytics_monthly_visits")
        .select("id, analytics_connection_id, month, visits")
        .order("month", { ascending: false })
        .limit(120);

      if (error) {
        throw error;
      }
      return (data ?? []) as AnalyticsMonthlyVisits[];
    }
  });
}
